import React, { useEffect, useMemo, useRef, useState } from "react";
import * as Mui from "@mui/material";
import { useSelector } from "react-redux";
import {
  AccountBalanceOutlined,
  AccountBalanceWalletOutlined,
  AttachFileOutlined,
  CalendarMonthOutlined,
  CategoryOutlined,
  CloseOutlined,
  EditOutlined,
  DescriptionOutlined,
  EventRepeatOutlined,
  FileUploadOutlined,
  NotesOutlined,
  PaymentsOutlined,
  TaskAltOutlined,
} from "@mui/icons-material";
import { detailAccentPalette, detailCardSx } from "components/DesignSystem/RequestDetailField";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestDetailsSection } from "components/DesignSystem/RequestDetailsSection";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { ViewActionFooter } from "../Shared/ViewActionFooter";
import { RequestDetailHeader } from "components/DesignSystem/RequestDetailHeader";
import { RequestStatusChip } from "components/DesignSystem/RequestStatusChip";
import { globalTextFieldSx, sharedTableBodyCellSx } from "components/DesignSystem/sharedUiPatterns";
import { SelectedEmployeeHeader } from "pages/TeamZone/Approve/SelectedEmployeeHeader";
import { PanelActionFooter } from "components/CustomComponents/DesignSystemButtons";
import { CustomAutoCompleteWithLogo } from "components/CustomComponents/CustomAutoCompleteWithLogo";
import { Loader, LoaderWithoutHeight } from "components/Loader";
import { tooltipStyleProps } from "services/constants/Styles";
import { designColors, designRadii, designShell, designType } from "services/constants/designTokens";
import { SelectField, TextField } from "../Shared/fieldInputs";
import { Field, FormSection, panelBodySx } from "../Shared/formUi";
import { formatDate, formatMoney } from "../Shared/formatters";
import { loadShiftFormOptions } from "../ShiftEarnings/shiftEarningsApi";
import { PayInputActivityLog } from "../Shared/PayInputActivityLog";
import { ATTACHMENT_TYPES, TEXT_MAX, loanInstalment, monthLabel, monthOptions, validateLoanForm } from "./loansData";
import { ATTACHMENT_AVAILABLE, ENDED_STATUSES, getAdvanceRequest } from "./loansApi";
import { ComingSoonChip } from "../../Shared/ConfigurationUi";

// Pay Inputs > Loans & Advances side panels, same layout as the other Pay
// Inputs panels (white section cards on the page background, one-line
// helper under every label).

// Used when the API row has no color_code of its own.
export const STATUS_COLORS = {
  Pending: "#F79009",
  Approved: "#0BB783",
  Disbursed: "#3699FF",
  // Set when a loan / advance is disbursed (Activity History API).
  Active: "#3699FF",
  Closed: "#6B7280",
  "Pre-Closed": "#6B7280",
  Cancelled: "#DC2626",
  Rejected: "#DC2626",
};
export const statusColor = (row) => row?.color_code || STATUS_COLORS[row?.status] || "#6B7280";

export const required = (label) => (
  <>
    {label} <Mui.Box component="span" sx={{ color: "#DC2626" }}>*</Mui.Box>
  </>
);

const TYPE_CARDS = {
  Loan: { icon: AccountBalanceOutlined, accent: "#2878FF", tint: "#EAF2FF", text: "Repaid through payroll, in full or in monthly instalments." },
  Advance: { icon: AccountBalanceWalletOutlined, accent: "#7A5AF8", tint: "#F1EDFF", text: "Salary advance with no repayment schedule." },
};

// Loan / Advance picker as two option cards (from the mockup).
export const TypeCards = ({ value, onChange, disabled }) => (
  <Mui.Box role="radiogroup" sx={{ gridColumn: "1 / -1", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
    {Object.entries(TYPE_CARDS).map(([type, card]) => {
      const active = value === type;
      return (
        <Mui.ButtonBase
          key={type}
          role="radio"
          aria-checked={active}
          disabled={disabled}
          onClick={() => onChange(type)}
          sx={{
            display: "block",
            textAlign: "left",
            p: 1.5,
            borderRadius: designRadii.card,
            border: `1.5px solid ${active ? designColors.teal : designColors.slate200}`,
            backgroundColor: active ? designColors.tealTint : designColors.white,
            opacity: disabled && !active ? 0.6 : 1,
          }}
        >
          <Mui.Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Mui.Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: designRadii.iconButton,
                backgroundColor: card.tint,
                color: card.accent,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <card.icon fontSize="small" />
            </Mui.Box>
            <Mui.Radio
              size="small"
              checked={active}
              disabled={disabled}
              tabIndex={-1}
              sx={{ p: 0, color: designColors.slate300, "&.Mui-checked": { color: designColors.tealDark } }}
            />
          </Mui.Stack>
          <Mui.Typography sx={{ ...designType.sectionHeading, mt: 1 }}>{type}</Mui.Typography>
          <Mui.Typography sx={{ ...designType.captionText, mt: 0.25 }}>{card.text}</Mui.Typography>
        </Mui.ButtonBase>
      );
    })}
  </Mui.Box>
);

// Saved files can come back as a path ("/media/..."): link them on the domain.
export const fileHref = (domain, url) => (!url || /^https?:\/\//i.test(url) ? url : `${(domain || "").replace(/\/$/, "")}/${url.replace(/^\//, "")}`);
const fileName = (url) => decodeURIComponent((url || "").split("?")[0].split("/").pop() || "Attachment");

// Upload box from the mockup: click or drop one PDF / JPG / PNG (max 5 MB).
// A file already saved on the request is shown until a new one is picked.
const AttachmentField = ({ file, savedUrl, onChange, error, disabled }) => {
  const { domain } = useSelector((state) => state.tokenReducer);
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const pick = (picked) => { if (picked) onChange(picked); };
  return (
    <Mui.Stack spacing={1}>
      <Mui.Box
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        onClick={() => !disabled && inputRef.current?.click()}
        onKeyDown={(event) => { if (!disabled && (event.key === "Enter" || event.key === " ")) { event.preventDefault(); inputRef.current?.click(); } }}
        onDragOver={(event) => { event.preventDefault(); if (!disabled) setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => { event.preventDefault(); setDragging(false); if (!disabled) pick(event.dataTransfer.files?.[0]); }}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1.5,
          py: 2,
          px: 2,
          borderRadius: designRadii.card,
          border: `1.5px dashed ${error ? "#DC2626" : dragging ? designColors.teal : designColors.slate300}`,
          backgroundColor: dragging ? designColors.tealTint : designColors.slateBg,
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.7 : 1,
        }}
      >
        <FileUploadOutlined sx={{ color: designColors.slate400 }} />
        <Mui.Box>
          <Mui.Typography sx={designType.helperText}>Drag and drop a file here or click to upload</Mui.Typography>
          <Mui.Typography sx={designType.captionText}>PDF, JPG, PNG (Max 5 MB)</Mui.Typography>
        </Mui.Box>
        <input
          ref={inputRef}
          type="file"
          hidden
          accept={ATTACHMENT_TYPES.join(",")}
          onChange={(event) => { pick(event.target.files?.[0]); event.target.value = ""; }}
        />
      </Mui.Box>
      {file || savedUrl ? (
        <Mui.Stack direction="row" alignItems="center" spacing={1} sx={{ ...detailCardSx, py: 1, px: 1.5 }}>
          <AttachFileOutlined fontSize="small" sx={{ color: designColors.slate400 }} />
          {file ? (
            <Mui.Tooltip title={file.name} arrow placement="top" slotProps={tooltipStyleProps}>
              <Mui.Typography sx={{ ...designType.helperText, flex: 1, minWidth: 0 }} noWrap>{file.name}</Mui.Typography>
            </Mui.Tooltip>
          ) : (
            <Mui.Link href={fileHref(domain, savedUrl)} target="_blank" rel="noopener noreferrer" sx={{ ...designType.helperText, flex: 1, minWidth: 0 }} noWrap>
              {fileName(savedUrl)}
            </Mui.Link>
          )}
          {file ? (
            <Mui.IconButton size="small" aria-label="Remove file" disabled={disabled} onClick={() => onChange(null)}>
              <CloseOutlined fontSize="small" />
            </Mui.IconButton>
          ) : null}
        </Mui.Stack>
      ) : null}
    </Mui.Stack>
  );
};

// Multi-line text with a character counter.
export const TextAreaField = ({ value, onChange, placeholder, error, disabled, max = TEXT_MAX }) => (
  <Mui.TextField
    fullWidth
    multiline
    minRows={3}
    size="small"
    value={value}
    placeholder={placeholder}
    disabled={disabled}
    error={Boolean(error)}
    onChange={(event) => onChange(event.target.value.slice(0, max))}
    helperText={`${value.length}/${max}`}
    FormHelperTextProps={{ sx: { textAlign: "right", mx: 0 } }}
    sx={globalTextFieldSx}
  />
);

// ---- Add / Edit Loan or Advance ----
// Saved through POST / PUT advance/request/. Only Pending requests are
// edited (the list hides Edit otherwise), and the employee and type stay.
export const LoanFormPanel = ({ open, mode, initial, onClose, onSave, expanded, onToggleExpand, saving }) => {
  const { domain, topbarCompanyId } = useSelector((state) => state.tokenReducer);
  const [employees, setEmployees] = useState([]);
  const [employeesError, setEmployeesError] = useState("");
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [retry, setRetry] = useState(0);
  const [form, setForm] = useState(initial);
  const [showErrors, setShowErrors] = useState(false);

  // Employees load when the panel opens (same list as the other Pay Inputs).
  useEffect(() => {
    if (!open || mode === "edit") return undefined;
    const controller = new AbortController();
    setEmployeesLoading(true);
    setEmployeesError("");
    loadShiftFormOptions(domain, topbarCompanyId, controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return;
        setEmployees(data.employees);
        setEmployeesError(data.errors.find((text) => text.includes("employees")) || "");
      })
      .catch((error) => { if (!controller.signal.aborted) setEmployeesError(error.message); })
      .finally(() => { if (!controller.signal.aborted) setEmployeesLoading(false); });
    return () => controller.abort();
  }, [open, mode, domain, topbarCompanyId, retry]);

  useEffect(() => {
    if (open) {
      setForm(initial);
      setShowErrors(false);
    }
  }, [open, initial]);

  const errors = useMemo(() => (form ? validateLoanForm(form) : {}), [form]);
  if (!form) return null;

  const set = (key) => (value) => setForm((current) => ({ ...current, [key]: value }));
  const visibleError = (key) => (showErrors ? errors[key] : undefined);
  const submit = () => {
    if (Object.keys(errors).length) {
      setShowErrors(true);
      return;
    }
    onSave(form);
  };

  const editing = mode === "edit";
  const isLoan = form.record_type === "Loan";
  const instalment = loanInstalment(form);
  const word = isLoan ? "Loan" : "Advance";

  // The edited request's employee is shown from the row itself.
  const options = editing
    ? [{ value: form.employee_code, id: form.employee_code, employee_id: form.employee_code, user_id: form.user_id, label: form.employee_name || form.employee_code, image: form.image_url || "", image_url: form.image_url || "", department_name: form.department_name, designation: form.designation }]
    : employees;
  const selected = options.find((option) => option.value === form.employee_code) ?? null;

  return (
    <GlobalSidePanel
      open={open}
      onClose={saving ? undefined : onClose}
      expanded={expanded}
      onToggleExpand={onToggleExpand}
      title={editing ? `Edit ${word}` : "Add Loan / Advance"}
      subtitle={editing ? "Update this pending request." : "Raise a loan or salary advance request. It goes to the employee's approver."}
      bodySx={panelBodySx}
      footer={
        <PanelActionFooter
          onCancel={saving ? undefined : onClose}
          primaryLabel={editing ? "Save Changes" : `Add ${word}`}
          primaryProps={{ onClick: submit, loading: saving, disabled: saving || employeesLoading }}
        />
      }
    >
      {/* Employees load first; the form shows once they are in (same as
          the other Pay Inputs panels). */}
      {employeesLoading ? <LoaderWithoutHeight info="Loading..." /> : (
      <Mui.Stack spacing={2}>
        {employeesError ? (
          <RequestInfoBanner
            tone="danger"
            title="Employees could not be loaded"
            body={
              <>
                {employeesError}{" "}
                <Mui.Link component="button" onClick={() => setRetry((value) => value + 1)}>Try again</Mui.Link>
              </>
            }
          />
        ) : null}
        <FormSection title="Employee">
          <Mui.Box
            sx={{
              gridColumn: "1 / -1",
              ...(editing ? { "& .MuiInputBase-input.Mui-disabled": { color: designColors.slate900, WebkitTextFillColor: designColors.slate900, opacity: 1 } } : {}),
            }}
          >
            <Field label={required("Employee")} helper="Employee who receives the money. Search by name or employee code." error={visibleError("employee_code")}>
              <CustomAutoCompleteWithLogo
                  dataList={options}
                  selectedValue={selected}
                  updateDataFunction={(event, option) =>
                    setForm((current) => ({ ...current, employee_code: option?.value ?? "", user_id: option?.user_id ?? "" }))
                  }
                  placeHolder="Select employee"
                  textBoxError={Boolean(visibleError("employee_code"))}
                  disableAction={editing || saving}
                  showAvathar={!selected}
                />
            </Field>
          </Mui.Box>
        </FormSection>
        {selected ? <SelectedEmployeeHeader selectedUser={selected} /> : null}

        <FormSection title="Request Type" info="A loan is repaid through payroll; a salary advance has no repayment schedule.">
          <TypeCards
            value={form.record_type}
            disabled={saving || editing}
            onChange={(type) => setForm((current) => ({ ...current, record_type: type }))}
          />
        </FormSection>

        {!isLoan ? (
          <RequestInfoBanner
            title="How an advance works"
            body="An advance is paid in the selected month's payroll and settled in the same month. It does not have a repayment schedule."
          />
        ) : null}

        <FormSection title={`${word} Details`}>
          <Field label={`${word} Description`} helper="Short name or reference for this request." error={visibleError("description")} wide>
            <TextField
              value={form.description}
              disabled={saving}
              error={visibleError("description")}
              placeholder={isLoan ? "e.g. Two-wheeler loan" : "e.g. Emergency advance"}
              onChange={(value) => set("description")(value.slice(0, TEXT_MAX))}
            />
          </Field>
          <Field label={required(`${word} Amount (₹)`)} helper="Total amount given." error={visibleError("amount")} wide={!isLoan}>
            <TextField type="number" value={form.amount} disabled={saving} error={visibleError("amount")} onChange={set("amount")} />
          </Field>
          {!isLoan ? (
            <Field label={required("Payroll Month")} helper="Payroll the advance is paid and settled in." error={visibleError("payroll_month")} wide>
              <SelectField
                value={form.payroll_month}
                options={monthOptions([initial?.payroll_month])}
                disabled={saving}
                error={visibleError("payroll_month")}
                placeholder="Select month"
                onChange={set("payroll_month")}
              />
            </Field>
          ) : null}
          {isLoan ? (
            <>
              <Field label={required("Repayment Start Month")} helper="First payroll deduction." error={visibleError("start_month")}>
                <SelectField
                  value={form.start_month}
                  onChange={set("start_month")}
                  options={monthOptions([initial?.start_month])}
                  disabled={saving}
                  error={visibleError("start_month")}
                  placeholder="Select month"
                />
              </Field>
              <Field label={required("Repayment Period")} helper="1 to 60 months." error={visibleError("months")}>
                <TextField type="number" value={form.months} disabled={saving} error={visibleError("months")} onChange={set("months")} />
              </Field>
              <Field label="Monthly Instalment (₹)" helper="Amount ÷ months (auto).">
                <TextField value={instalment ? formatMoney(instalment) : "–"} disabled onChange={() => {}} />
              </Field>
            </>
          ) : null}
        </FormSection>

        <FormSection title="Reason & Attachment">
          <Field label={required("Reason")} helper="Why the employee needs the money." error={visibleError("reason")} wide>
            <TextAreaField value={form.reason} disabled={saving} error={visibleError("reason")} placeholder="Enter the reason" onChange={set("reason")} />
          </Field>
          <Field
            label={<>Attachment (Optional){ATTACHMENT_AVAILABLE ? null : <ComingSoonChip />}</>}
            helper={ATTACHMENT_AVAILABLE ? "Supporting document, e.g. a quotation or medical bill." : "File upload isn't available yet."}
            error={visibleError("attachment")}
            wide
          >
            <AttachmentField
              file={form.attachment}
              savedUrl={form.attachment_url}
              disabled={saving || !ATTACHMENT_AVAILABLE}
              error={visibleError("attachment")}
              onChange={set("attachment")}
            />
          </Field>
        </FormSection>
      </Mui.Stack>
      )}
    </GlobalSidePanel>
  );
};

// ---- View ----

const scheduleHeadSx = { ...designShell.tableHeadCellBase, py: 1, whiteSpace: "nowrap" };
const scheduleCellSx = { ...sharedTableBodyCellSx, py: 1 };
const SCHEDULE_COLORS = { Paid: "#0BB783", Pending: "#3699FF", Skipped: "#F79009" };

// Repayment schedule from GET advance/request/{id}/.
const RepaymentSchedule = ({ row, detail, loading, error, onRetry }) => {
  if (row.record_type !== "Loan") {
    return <RequestInfoBanner title="No repayment schedule" body="A salary advance has no repayment schedule." />;
  }
  if (loading) return <Loader info="Loading..." minHeight="160px" />;
  if (error) {
    return (
      <RequestInfoBanner
        tone="danger"
        title="The schedule could not be loaded"
        body={<>{error} <Mui.Link component="button" onClick={onRetry}>Try again</Mui.Link></>}
      />
    );
  }
  const schedule = detail?.repayment_schedule ?? [];
  if (!schedule.length) {
    return <RequestInfoBanner title="No schedule yet" body="The instalments are listed here once the server has built them." />;
  }
  return (
    <Mui.Box sx={{ ...detailCardSx, p: 0, overflow: "auto" }}>
      <Mui.Table size="small">
        <Mui.TableHead>
          <Mui.TableRow>
            <Mui.TableCell sx={scheduleHeadSx}>#</Mui.TableCell>
            <Mui.TableCell sx={scheduleHeadSx}>Due Date</Mui.TableCell>
            <Mui.TableCell sx={{ ...scheduleHeadSx, textAlign: "right" }}>Amount Due</Mui.TableCell>
            <Mui.TableCell sx={{ ...scheduleHeadSx, textAlign: "right" }}>Paid</Mui.TableCell>
            <Mui.TableCell sx={{ ...scheduleHeadSx, textAlign: "right" }}>Balance</Mui.TableCell>
            <Mui.TableCell sx={scheduleHeadSx}>Status</Mui.TableCell>
          </Mui.TableRow>
        </Mui.TableHead>
        <Mui.TableBody>
          {schedule.map((entry, index) => (
            <Mui.TableRow key={entry.id ?? index} sx={{ "&:last-child td": { borderBottom: "none" } }}>
              <Mui.TableCell sx={scheduleCellSx}>{index + 1}</Mui.TableCell>
              <Mui.TableCell sx={scheduleCellSx}>{entry.due_date ? formatDate(entry.due_date) : "–"}</Mui.TableCell>
              <Mui.TableCell sx={{ ...scheduleCellSx, textAlign: "right" }}>{formatMoney(entry.amount_due)}</Mui.TableCell>
              <Mui.TableCell sx={{ ...scheduleCellSx, textAlign: "right" }}>{formatMoney(entry.amount_paid)}</Mui.TableCell>
              <Mui.TableCell sx={{ ...scheduleCellSx, textAlign: "right" }}>{formatMoney(entry.balance)}</Mui.TableCell>
              <Mui.TableCell sx={scheduleCellSx}>
                <RequestStatusChip status={entry.status || "–"} colorCode={SCHEDULE_COLORS[entry.status] || "#6B7280"} />
              </Mui.TableCell>
            </Mui.TableRow>
          ))}
        </Mui.TableBody>
      </Mui.Table>
    </Mui.Box>
  );
};

// Edit while Pending; Cancel until the request has ended or anything has
// been repaid.
export const canEditRequest = (row) => row?.status === "Pending";
export const canCancelRequest = (row) => Boolean(row) && !ENDED_STATUSES.includes(row.status) && !(row.repaid_amount > 0);

// showApproval: false when Pay Input Configuration has no approval step, so
// the status chip is hidden (same as Shift Earnings / Leave).
export const LoanViewPanel = ({ open, row, onClose, onEdit, onCancel, onStatus, canEdit, showApproval = true, expanded, onToggleExpand, busy }) => {
  const { domain, topbarCompanyId } = useSelector((state) => state.tokenReducer);
  const [tab, setTab] = useState("info");
  const [detail, setDetail] = useState(null);
  const [detailError, setDetailError] = useState("");
  const [detailLoading, setDetailLoading] = useState(false);
  const [retry, setRetry] = useState(0);
  useEffect(() => setTab("info"), [row?.id]);

  // The schedule comes with the request's own details.
  useEffect(() => {
    setDetail(null);
    setDetailError("");
    if (!open || row?.id == null || row.record_type !== "Loan") return undefined;
    const controller = new AbortController();
    setDetailLoading(true);
    getAdvanceRequest(domain, topbarCompanyId, row.id, controller.signal)
      .then((data) => { if (!controller.signal.aborted) setDetail(data); })
      .catch((error) => { if (!controller.signal.aborted) setDetailError(error.message); })
      .finally(() => { if (!controller.signal.aborted) setDetailLoading(false); });
    return () => controller.abort();
  }, [open, row?.id, row?.record_type, domain, topbarCompanyId, retry]);

  if (!row) return null;

  const { indigo, blue, green, orange, pink } = detailAccentPalette;
  const isLoan = row.record_type === "Loan";
  const cancellable = canCancelRequest(row);
  const repayment = isLoan
    ? [
        { icon: EventRepeatOutlined, label: "Repayment", value: row.instalment_count > 1 ? `${row.instalment_count} monthly instalments` : row.repayment_mode || "Full", accent: blue },
        { icon: CalendarMonthOutlined, label: "Repayment Starts", value: row.repayment_date ? monthLabel(row.repayment_date.slice(0, 7)) : "–", accent: indigo },
      ]
    : [{ icon: CalendarMonthOutlined, label: "Payroll Month", value: (row.payroll_month || row.repayment_date) ? monthLabel((row.payroll_month || row.repayment_date).slice(0, 7)) : "–", accent: indigo }];

  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      expanded={expanded}
      onToggleExpand={onToggleExpand}
      title={row.record_type}
      subtitle={`${row.advance_type} ${row.record_type.toLowerCase()} · requested ${formatDate(row.issue_date)}`}
      bodySx={panelBodySx}
      headerActions={
        canEdit && canEditRequest(row) ? (
          <Mui.Tooltip title="Edit" arrow slotProps={tooltipStyleProps}>
            <span>
              <Mui.IconButton
                size="small"
                aria-label="Edit"
                disabled={busy}
                onClick={() => onEdit(row)}
                sx={{ ...designShell.panelSoftIconButton, color: designColors.tealDark }}
              >
                <EditOutlined fontSize="small" />
              </Mui.IconButton>
            </span>
          </Mui.Tooltip>
        ) : null
      }
      // Approve / Reject (Pending requests, when approval is on) above the
      // full-width Cancel.
      footer={
        canEdit && !ENDED_STATUSES.includes(row.status) ? (
          <ViewActionFooter
            approval={
              showApproval && row.status === "Pending" && onStatus
                ? { status: row.status, disabled: busy, onApprove: () => onStatus(row, "Approved"), onReject: () => onStatus(row, "Rejected") }
                : null
            }
            recordAction={{
              label: `Cancel ${row.record_type}`,
              onClick: () => onCancel(row),
              disabled: busy || !cancellable,
              tooltip: cancellable ? "" : "Part of this has already been repaid, so it can't be cancelled.",
            }}
          />
        ) : null
      }
    >
      <Mui.Box sx={{ mb: 2 }}>
        <RequestDetailHeader
          userDetails={{
            image_url: row.image_url,
            employee_id: row.employee_code,
            user_name: row.employee_name,
            department_name: row.department,
            designation: row.designation,
          }}
          status={showApproval ? row.status : undefined}
          colorCode={statusColor(row)}
        />
      </Mui.Box>
      <Mui.Tabs
        value={tab}
        onChange={(event, value) => setTab(value)}
        variant="fullWidth"
        sx={{ ...designShell.panelTabs, mt: 0, mb: 2, borderBottom: `1px solid ${designColors.slate200}` }}
      >
        <Mui.Tab label="Information" value="info" />
        <Mui.Tab label="Repayment Schedule" value="schedule" />
        <Mui.Tab label="Activity Log" value="audit" />
      </Mui.Tabs>
      {tab === "audit" ? (
        <PayInputActivityLog domain={domain} type="LOAN_ADVANCE" recordId={row.id} refreshKey={row.modifiedat ?? row.updated_at} />
      ) : tab === "schedule" ? (
        <RepaymentSchedule row={row} detail={detail} loading={detailLoading} error={detailError} onRetry={() => setRetry((value) => value + 1)} />
      ) : (
        <Mui.Stack spacing={2}>
          <RequestDetailsSection
            title={`${row.record_type.toUpperCase()} DETAILS`}
            fields={[
              { icon: CategoryOutlined, label: "Type", value: `${row.advance_type} ${row.record_type}`, accent: pink },
              { icon: DescriptionOutlined, label: "Description / Reference", value: row.description || "–", accent: indigo },
              { icon: CalendarMonthOutlined, label: "Request Date", value: formatDate(row.issue_date), accent: blue },
              ...repayment,
            ]}
          />
          <RequestDetailsSection
            title="AMOUNTS"
            fields={[
              { icon: PaymentsOutlined, label: "Amount", value: formatMoney(row.disbursed_amount), accent: indigo },
              { icon: TaskAltOutlined, label: "Repaid", value: `${formatMoney(row.repaid_amount)} (${row.progress_pct}%)`, accent: green },
              { icon: AccountBalanceWalletOutlined, label: "Balance", value: formatMoney(row.outstanding_amount), accent: orange },
            ]}
          />
          {row.reason ? (
            <RequestDetailsSection title="REASON" fields={[{ icon: NotesOutlined, label: "Reason", value: row.reason, accent: blue }]} />
          ) : null}
          {row.attachment_url ? (
            <RequestDetailsSection
              title="ATTACHMENT"
              fields={[{
                icon: AttachFileOutlined,
                label: "File",
                value: <Mui.Link href={fileHref(domain, row.attachment_url)} target="_blank" rel="noopener noreferrer">{fileName(row.attachment_url)}</Mui.Link>,
                accent: blue,
              }]}
            />
          ) : null}
        </Mui.Stack>
      )}
    </GlobalSidePanel>
  );
};
